import React, { useRef, useState } from "react";

import axios, { isAxiosError } from "axios";
import { SyncOutlined } from "@ant-design/icons";
import toast from "react-hot-toast";

import ButtonSmall from "@/common/ButtonSmall/ButtonSmall";
import Text from "@/common/Text/Text";
import ControlPanelActionRow from "@/features/ui/modals/components/ControlPanelActionRow/ControlPanelActionRow";
import { useListBrandsQuery } from "@/features/brands/brandsApi";
import {
  useClearBrandScreensaverAssignmentMutation,
  useCreateScreensaverMutation,
  useGetBrandScreensaverAssignmentQuery,
  useGetGlobalScreensaverAssignmentQuery,
  useSetBrandScreensaverAssignmentMutation,
  useSetGlobalScreensaverAssignmentMutation,
  useSignScreensaverUploadMutation,
} from "@/features/screensavers/screensaversApi";
import {
  planBrandScreensaverSave,
  replaceGlobalScreensaver,
  uploadScreensaverLibraryFile,
} from "@/features/screensavers/screensaverAssignmentModel";

import styles from "../ui/modals/components/DevicesControlPanel/DevicesControlPanel.module.scss";

function errorMessage(error: unknown, fallback: string): string {
  return (
    (isAxiosError(error) &&
      (error.response?.data as { message?: string } | undefined)?.message) ||
    (error as { data?: { message?: string } })?.data?.message ||
    (error instanceof Error ? error.message : fallback)
  );
}

/** Super Admin global default and brand assignment. No device commands. */
const ScreensaverAssignmentSections: React.FC<{ commandsEnabled: boolean }> = ({
  commandsEnabled,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const brandFileRef = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState(false);
  const [brandId, setBrandId] = useState("");
  const [brandBusy, setBrandBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: globalAssignment, isFetching } = useGetGlobalScreensaverAssignmentQuery();
  const { data: brandsData } = useListBrandsQuery({ view: "active" });
  const { data: brandAssignment, isFetching: brandFetching } =
    useGetBrandScreensaverAssignmentQuery(brandId, { skip: !brandId });
  const [signUpload] = useSignScreensaverUploadMutation();
  const [createScreensaver] = useCreateScreensaverMutation();
  const [setGlobal] = useSetGlobalScreensaverAssignmentMutation();
  const [setBrand] = useSetBrandScreensaverAssignmentMutation();
  const [clearBrand] = useClearBrandScreensaverAssignmentMutation();

  const asset = globalAssignment?.effective;
  const versionLabel =
    asset?.versionName || globalAssignment?.versionName || "Not loaded";
  const inherit =
    globalAssignment?.inheritingBrandCount == null
      ? "Unavailable"
      : String(globalAssignment.inheritingBrandCount);

  const handleReplace = async (file: File | null) => {
    if (!file || !commandsEnabled) return;
    setReplacing(true);
    try {
      await replaceGlobalScreensaver({
        file,
        sign: async (filename) => {
          const signed = await signUpload({ filename }).unwrap();
          return { url: signed.url };
        },
        put: async (url, body) => {
          await axios.put(url, body, { headers: { "Content-Type": "video/mp4" } });
        },
        create: async (filename) => {
          const created = await createScreensaver({
            filename,
            name: filename.replace(/\.mp4$/i, ""),
            displayProfileId: "rk3568_player",
          }).unwrap();
          return { releaseId: created.screensaver.id };
        },
        setGlobal: async (releaseId) => {
          await setGlobal({ releaseId }).unwrap();
        },
      });
      toast.success("Global default updated. Devices are not changed.");
    } catch (error) {
      toast.error(String(errorMessage(error, "Upload failed")));
    } finally {
      setReplacing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleBrandFile = async (file: File | null) => {
    if (!file || !brandId || !commandsEnabled) return;
    setBrandBusy(true);
    try {
      const plan = planBrandScreensaverSave({
        mode: "custom",
        hasNewFile: true,
        currentSource: brandAssignment?.source || "global",
      });
      if (plan !== "upload-override") return;
      const uploaded = await uploadScreensaverLibraryFile({
        file,
        sign: async (filename) => {
          const signed = await signUpload({ filename }).unwrap();
          return { url: signed.url };
        },
        put: async (url, body) => {
          await axios.put(url, body, { headers: { "Content-Type": "video/mp4" } });
        },
        create: async (filename) => {
          const created = await createScreensaver({
            filename,
            name: filename.replace(/\.mp4$/i, ""),
            displayProfileId: "rk3568_player",
          }).unwrap();
          return { releaseId: created.screensaver.id };
        },
      });
      await setBrand({ brandId, releaseId: uploaded.releaseId }).unwrap();
      toast.success("Brand screensaver saved. Devices are not changed.");
    } catch (error) {
      toast.error(String(errorMessage(error, "Upload failed")));
    } finally {
      setBrandBusy(false);
      if (brandFileRef.current) brandFileRef.current.value = "";
    }
  };

  const handleResetBrand = async () => {
    if (!brandId || !commandsEnabled) return;
    setBrandBusy(true);
    try {
      await clearBrand(brandId).unwrap();
      toast.success("Brand inherits the global default. Devices are not changed.");
    } catch (error) {
      toast.error(String(errorMessage(error, "Reset failed")));
    } finally {
      setBrandBusy(false);
    }
  };

  return (
    <>
      <div className="mb-3" data-testid="screensaver-global-section">
        <Text type="title" size="md">
          Global Default Screensaver
        </Text>
        <Text type="text" size="sm" className="d-block mb-2">
          KrossKast default for every brand that has not uploaded its own.
        </Text>
        <ControlPanelActionRow
          className={styles.firstActionRow}
          leftComponent={<Text size="md">Current default</Text>}
          rightComponent={
            <div data-testid="screensaver-global-version">
              <Text size="md">
                {versionLabel}
                {isFetching && <SyncOutlined className={styles.syncLoader} spin />}
              </Text>
            </div>
          }
        />
        <ControlPanelActionRow
          leftComponent={<Text size="md">Brands inheriting</Text>}
          rightComponent={
            <div data-testid="screensaver-global-inherit-count">
              <Text size="md">{inherit}</Text>
            </div>
          }
        />
        <ControlPanelActionRow
          leftComponent={<Text size="md">Preview</Text>}
          rightComponent={
            <div className="d-grid justify-content-end">
              <ButtonSmall
                type="button"
                disabled={!asset?.mediaUrl}
                data-testid="screensaver-global-preview"
                onClick={() => setPreviewUrl(asset?.mediaUrl || null)}
              >
                <Text size="md">Preview</Text>
              </ButtonSmall>
            </div>
          }
        />
        <ControlPanelActionRow
          borderBottom={false}
          leftComponent={
            <div className="d-flex align-items-center">
              <Text size="md">Upload / Replace Default</Text>
              {replacing && <SyncOutlined className={styles.syncLoader} spin />}
            </div>
          }
          rightComponent={
            <div className="d-grid justify-content-end">
              <input
                ref={fileRef}
                type="file"
                accept="video/mp4,.mp4"
                data-testid="screensaver-global-file"
                style={{ display: "none" }}
                onChange={(e) => handleReplace(e.target.files?.[0] || null)}
              />
              <ButtonSmall
                type="button"
                disabled={!commandsEnabled || replacing}
                data-testid="screensaver-global-replace"
                onClick={() => fileRef.current?.click()}
              >
                <Text size="md">Upload / Replace Default</Text>
              </ButtonSmall>
            </div>
          }
        />
        {previewUrl && (
          <video
            className="mt-2"
            data-testid="screensaver-global-preview-video"
            src={previewUrl}
            controls
            muted
            width={320}
          />
        )}
      </div>

      <div className="mb-3" data-testid="screensaver-brand-section">
        <Text type="title" size="md">
          Brand Screensaver
        </Text>
        <Text type="text" size="sm" className="d-block mb-2">
          Effective video for the selected brand. Super Admin can set or reset
          it here. This does not send the file to a device.
        </Text>
        <ControlPanelActionRow
          className={styles.firstActionRow}
          leftComponent={<Text size="md">Brand</Text>}
          rightComponent={
            <select
              className="form-select form-select-sm"
              data-testid="screensaver-brand-select"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
            >
              <option value="">Select a brand</option>
              {(brandsData?.brands || []).map((brand) => (
                <option key={brand.brandId} value={brand.brandId}>
                  {brand.name}
                </option>
              ))}
            </select>
          }
        />
        <ControlPanelActionRow
          leftComponent={<Text size="md">Effective</Text>}
          rightComponent={
            <div data-testid="screensaver-brand-effective">
              <Text size="md">
                {!brandId
                  ? "Select a brand"
                  : brandFetching
                    ? "Loading…"
                    : brandAssignment?.effective?.versionName ||
                      brandAssignment?.versionName ||
                      "Global Default"}
              </Text>
            </div>
          }
        />
        <ControlPanelActionRow
          leftComponent={<Text size="md">Source</Text>}
          rightComponent={
            <div data-testid="screensaver-brand-source">
              <Text size="md">
                {brandAssignment?.sourceLabel || (brandId ? "—" : "—")}
              </Text>
            </div>
          }
        />
        <ControlPanelActionRow
          leftComponent={<Text size="md">Custom video</Text>}
          rightComponent={
            <div className="d-grid justify-content-end">
              <input
                ref={brandFileRef}
                type="file"
                accept="video/mp4,.mp4"
                style={{ display: "none" }}
                data-testid="screensaver-brand-file"
                onChange={(e) => handleBrandFile(e.target.files?.[0] || null)}
              />
              <ButtonSmall
                type="button"
                disabled={!commandsEnabled || !brandId || brandBusy}
                data-testid="screensaver-brand-upload"
                onClick={() => brandFileRef.current?.click()}
              >
                <Text size="md">
                  {brandBusy ? "Saving…" : "Upload Custom"}
                </Text>
              </ButtonSmall>
            </div>
          }
        />
        <ControlPanelActionRow
          borderBottom={false}
          leftComponent={<Text size="md">Reset</Text>}
          rightComponent={
            <div className="d-grid justify-content-end">
              <ButtonSmall
                type="button"
                disabled={
                  !commandsEnabled ||
                  !brandId ||
                  brandBusy ||
                  brandAssignment?.source !== "brand"
                }
                data-testid="screensaver-brand-reset"
                onClick={handleResetBrand}
              >
                <Text size="md">Reset to Global Default</Text>
              </ButtonSmall>
            </div>
          }
        />
      </div>
    </>
  );
};

export default ScreensaverAssignmentSections;
